import { useState, useEffect } from "react";
import { getSupabase } from "@/integrations/supabase/lazy";
import type { User } from "@supabase/supabase-js";
import { getSessionSafe, clearStaleAuthStorage } from "@/lib/authSession";

// Routes that require a session to render anything useful: on these, waiting for
// a user gesture before initializing auth leaves the page stuck on "Loading…".
const EAGER_AUTH_PREFIXES = ["/my-account", "/community", "/forum", "/reset-password"];

function needsEagerAuth() {
  if (typeof window === "undefined") return false;
  const path = window.location.pathname.toLowerCase();
  return EAGER_AUTH_PREFIXES.some((p) => path === p || path.startsWith(p + "/") || path.startsWith(p));
}

export function useCustomerAuth(options?: { immediate?: boolean }) {
  const immediate = options?.immediate ?? true;
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [accountStatus, setAccountStatus] = useState<string>("pending");
  const [sessionExpired, setSessionExpired] = useState(false);

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    let started = false;
    let removeVisibility: (() => void) | null = null;
    // Anonymous first paint must not initialize the full auth client. Start
    // only after real engagement; authenticated controls remain available as
    // soon as the visitor touches, scrolls, clicks, or uses the keyboard.
    const events: (keyof WindowEventMap)[] = ["wheel", "pointerdown", "touchstart", "keydown"];
    const start = async () => {
      if (cancelled || started) return;
      started = true;
      events.forEach((event) => window.removeEventListener(event, start));
      let supabase;
      try {
        supabase = await getSupabase();
      } catch {
        if (!cancelled) {
          setUser(null);
          setSessionExpired(true);
          setLoading(false);
        }
        return;
      }
      if (cancelled) return;
      const { data: { subscription } } = supabase.auth.onAuthStateChange(
        (_event, session) => {
          setUser(session?.user ?? null);
          if (session?.user) setSessionExpired(false);
          setLoading(false);
        }
      );
      unsubscribe = () => subscription.unsubscribe();

      // Bounded session resolution: a tab idle for hours can leave getSession()
      // rejecting or hanging. Either way, stop loading and treat the visitor as
      // signed out so the layout can redirect to sign-in.
      const resolve = async () => {
        const { session, failed, reason } = await getSessionSafe(supabase!);
        if (cancelled) return;
        if (failed) {
          if (reason === "error") clearStaleAuthStorage();
          setUser(null);
          setSessionExpired(true);
          setLoading(false);
          return;
        }
        setUser(session?.user ?? null);
        setSessionExpired(false);
        setLoading(false);
      };
      await resolve();

      const onVisible = () => {
        if (document.visibilityState === "visible") void resolve();
      };
      document.addEventListener("visibilitychange", onVisible);
      removeVisibility = () => document.removeEventListener("visibilitychange", onVisible);
    };
    events.forEach((event) => window.addEventListener(event, start, { passive: true, once: true }));
    if (immediate && needsEagerAuth()) {
      void start();
    }
    return () => {
      cancelled = true;
      events.forEach((event) => window.removeEventListener(event, start));
      if (removeVisibility) removeVisibility();
      if (unsubscribe) unsubscribe();
    };
  }, [immediate]);

  useEffect(() => {
    if (!user) {
      setAccountStatus("pending");
      return;
    }
    let cancelled = false;
    getSupabase()
      .then((supabase) =>
        supabase
          .from("profiles")
          .select("account_status")
          .eq("id", user.id)
          .maybeSingle()
          .then(({ data }) => {
            if (!cancelled) {
              setAccountStatus((data as any)?.account_status ?? "pending");
            }
          })
      )
      .catch(() => {
        if (!cancelled) setAccountStatus("pending");
      });
    return () => { cancelled = true; };
  }, [user]);

  const isApproved = accountStatus === "approved";

  const signOut = async () => {
    const supabase = await getSupabase();
    await supabase.auth.signOut();
  };

  return { user, loading, accountStatus, isApproved, sessionExpired, signOut };
}
